import {
  useGetIdentity,
  useGetList,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { QueryFallback } from "@/components/admin/query-fallback";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { ContactNameLink, OpenTaskDue } from "../palmtree/badges";
import {
  contactListFromPath,
  isCreatableContactType,
} from "../palmtree/constants";
import { panelClass, rowClass } from "../palmtree/shell";
import { nullsLastMeta } from "../providers/commons/nullsLast";
import type { Contact, ContactNote, Task } from "../types";
import { ContactCreateDialog } from "./ContactCreate";

const rowMeta = (
  translate: (key: string, options?: Record<string, unknown>) => string,
  task?: Task,
  note?: ContactNote,
) => {
  const parts: string[] = [];
  if (task?.text) parts.push(task.text);
  if (note?.text) {
    parts.push(translate("palmtree.lists.last_note", { text: note.text }));
  }
  return parts.join(" · ");
};

export const ContactList = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const list = contactListFromPath(useLocation().pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement>(null);
  const addContactRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState("");
  const requestedType = searchParams.get("nieuw");
  const defaultType = isCreatableContactType(requestedType)
    ? requestedType
    : list.type;
  const [createOpen, setCreateOpen] = useState(
    isCreatableContactType(requestedType),
  );
  const focusSearch = searchParams.get("focus") === "1";

  const contactsQuery = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "last_name", order: "ASC" },
      filter: { type: list.type },
    },
    { retry: false },
  );
  const { data = [], isPending } = contactsQuery;
  const { data: tasks = [] } = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "due_date", order: "ASC" },
    filter: { "done_date@is": null },
  });
  const { data: notes = [] } = useGetList<ContactNote>("contact_notes", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "date", order: "DESC" },
    meta: nullsLastMeta,
  });

  const taskByContact = useMemo(() => {
    const map = new Map<Identifier, Task>();
    for (const task of tasks) {
      if (!map.has(task.contact_id)) map.set(task.contact_id, task);
    }
    return map;
  }, [tasks]);
  const noteByContact = useMemo(() => {
    const map = new Map<Identifier, ContactNote>();
    for (const note of notes) {
      if (!map.has(note.contact_id)) map.set(note.contact_id, note);
    }
    return map;
  }, [notes]);

  useEffect(() => {
    if (!focusSearch || list.type !== "person" || !searchRef.current) return;
    searchRef.current.focus();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("focus");
        return next;
      },
      { replace: true },
    );
  }, [focusSearch, identity, isPending, list.type, setSearchParams]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return data.filter((contact) => {
      if (!needle) return true;
      const haystack = [
        contact.first_name,
        contact.last_name,
        contact.company_name,
        contact.title,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [data, query]);

  return (
    <>
      <div className={`${panelClass} p-4 md:p-6`}>
        <div className="@container/list mb-4">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 @[480px]/list:grid-cols-[minmax(max-content,1fr)_minmax(0,14rem)_auto]">
            <h2 className="col-start-1 row-start-1 min-w-0 truncate font-heading text-lg font-bold tracking-tight @[480px]/list:text-xl">
              {translate(list.labelKey)}
            </h2>
            <Input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={translate(list.searchKey)}
              aria-label={translate(list.searchKey)}
              className="col-span-2 col-start-1 row-start-2 h-9 w-full min-w-0 @[480px]/list:col-span-1 @[480px]/list:col-start-2 @[480px]/list:row-start-1"
            />
            <Button
              ref={addContactRef}
              type="button"
              className="col-start-2 row-start-1 h-11 min-h-11 justify-self-end rounded-[12px] px-[14px] py-0 text-sm font-[650] md:h-[36px] md:min-h-[36px] @[480px]/list:col-start-3"
              onClick={() => setCreateOpen(true)}
            >
              {translate("palmtree.lists.add_contact")}
            </Button>
          </div>
        </div>
        <QueryFallback queries={[contactsQuery]}>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {query.trim()
                ? translate("palmtree.lists.no_results")
                : translate(list.emptyKey)}
            </p>
          ) : (
            <ul className="flex flex-col gap-2.5">
              {filtered.map((contact) => {
                const task = taskByContact.get(contact.id);
                const note = noteByContact.get(contact.id);
                const meta = rowMeta(translate, task, note);
                return (
                  <li
                    key={contact.id}
                    className={`${rowClass} flex items-center gap-3`}
                  >
                    <div className="min-w-0 flex-1">
                      <ContactNameLink contact={contact} />
                      {meta ? (
                        <p className="mt-1 text-[12.5px] text-muted-foreground">
                          {meta}
                        </p>
                      ) : null}
                    </div>
                    {task ? <OpenTaskDue dueDate={task.due_date} /> : null}
                  </li>
                );
              })}
            </ul>
          )}
        </QueryFallback>
      </div>
      <ContactCreateDialog
        open={createOpen}
        defaultType={defaultType}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          addContactRef.current?.focus();
        }}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open && searchParams.has("nieuw")) {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("nieuw");
                return next;
              },
              { replace: true },
            );
          }
        }}
      />
    </>
  );
};

export const ContactListMobile = ContactList;
